import React from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import MoodIcon from '@mui/icons-material/Mood';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Check, Description, Edit } from '@mui/icons-material';
import { border, dark, surface, teal } from '../../../assets/theme/Colors';

export default function GeneratedInitialPlan({
	plan,
	isLoading,
	onGoBack,
	elder,
	onPlanChange,
	editingSection,
	setEditingSection,
	onStorePlan,
	onDownload,
}) {
	const formattedDate = new Date().toLocaleDateString('en-US', {
		month: 'long',
		year: 'numeric',
	});

	function onRenderSections(sectionIndex, itemIndex, contentIndex, item) {
		return (
			<>
				{editingSection === sectionIndex ? (
					<TextField
						fullWidth
						multiline
						variant="outlined"
						value={item}
						onChange={e =>
							onPlanChange(
								e.target.value,
								sectionIndex,
								itemIndex,
								contentIndex,
							)
						}
						sx={{
							'& .MuiOutlinedInput-root': {
								fontSize: '13px',
								borderRadius: '8px',
								background: '#fff',
							},
						}}
					/>
				) : (
					<Typography
						sx={{
							fontSize: 13,
							color: '#6b8888',
							lineHeight: 1.5,
							pt: '6px',
						}}>
						{item}
					</Typography>
				)}
			</>
		);
	}

	return (
		<Box
			sx={{
				p: 3,
				backgroundColor: '#f8fafc',
				minHeight: '100vh',
				width: '100%',
			}}>
			{/* Back Link */}
			<Button
				onClick={onGoBack}
				startIcon={<ArrowBackIosNewIcon fontSize="small" />}
				disabled={isLoading}
				sx={{
					mb: 2,
					textTransform: 'none',
					color: '#475569',
					fontWeight: 500,
					fontSize: 12,
				}}>
				Back to Setup
			</Button>

			{/* Header */}
			<Stack
				direction={{ xs: 'column', md: 'row' }}
				justifyContent="space-between"
				alignItems={{ xs: 'flex-start', md: 'center' }}
				spacing={2}
				mb={3}>
				<Box>
					<Typography fontSize={'22px'} fontWeight={500} gutterBottom>
						{`Care Plan - ${elder}`}
					</Typography>

					<Typography fontSize={'13px'} color="text.secondary">
						{`AI-generated · ${formattedDate} · Review and edit each
						section before saving`}
					</Typography>
				</Box>

				<Stack
					direction="row"
					spacing={1}
					flexWrap="wrap"
					alignItems="center">
					<Chip
						icon={
							<AutoAwesomeIcon
								style={{ color: teal, fontSize: 16 }}
							/>
						}
						label="AI Generated"
						sx={{
							backgroundColor: dark,
							color: teal,
							fontWeight: 600,
						}}
					/>

					<Button
						variant="contained"
						startIcon={<SaveIcon />}
						onClick={onStorePlan}
						disabled={isLoading}
						sx={{
							textTransform: 'none',
							borderRadius: 2,
							backgroundColor: '#0f766e',
						}}>
						Save to Profile
					</Button>

					<Button
						onClick={onDownload}
						variant="outlined"
						startIcon={<DownloadIcon />}
						disabled={isLoading}
						sx={{
							textTransform: 'none',
							borderRadius: '8px',
							fontWeight: 500,
							fontSize: 13,
							px: 1.8,
							py: 0.9,
							boxShadow: 'none',
							border: `1px solid ${border}`,
							backgroundColor: '#fff',
							color: '#1a2424',
							'&:hover': {
								backgroundColor: surface,
								borderColor: teal,
								boxShadow: 'none',
							},
						}}>
						Download PDF
					</Button>
				</Stack>
			</Stack>

			{/* Alert */}
			{!isLoading && (
				<Alert
					icon={<CheckCircleIcon />}
					severity="success"
					sx={{
						mb: 3,
						borderRadius: 3,
					}}>
					{`Care plan generated across ${plan.length} categories. Review each
					section, edit if needed, then save to ${''}
					profile.`}
				</Alert>
			)}

			{/* Sections */}
			<Stack spacing={2.5}>
				{plan.length > 0 &&
					plan.map((section, sectionIndex) => (
						<Box
							key={section.title}
							sx={{
								border: '1px solid #d8e8e8',
								borderRadius: '12px',
								overflow: 'hidden',
								mb: 1.5,
							}}>
							{/* SECTION HEADER */}
							<Box
								sx={{
									background: '#e6f9f7',
									borderBottom: '1px solid #b3ede8',
									p: '12px 16px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
								}}>
								<Typography
									sx={{
										fontSize: 13,
										fontWeight: 700,
										color: '#0a5050',
									}}>
									{section.title}
								</Typography>

								<Button
									size="small"
									variant="outlined"
									startIcon={
										<Edit
											fontSize="small"
											sx={{ width: 12, height: 12 }}
										/>
									}
									onClick={() =>
										setEditingSection(
											editingSection === sectionIndex
												? null
												: sectionIndex,
										)
									}
									disabled={isLoading}
									sx={{
										minWidth: 0,
										height: 26,
										fontSize: 11,
										textTransform: 'none',
										borderRadius: '6px',
										borderColor: border,
										color: '#1a2424',
										background: '#fff',
									}}>
									{editingSection === sectionIndex
										? 'Done'
										: 'Edit'}
								</Button>
							</Box>

							{/* BODY */}
							<Box sx={{ p: '14px 16px' }}>
								{section.bodyContent.map((item, itemIndex) => (
									<Box key={itemIndex} sx={{ mb: 2 }}>
										{/* SUB TITLE */}
										{item.label && (
											<Typography
												sx={{
													fontSize: 12,
													fontWeight: 700,
													color: '#0a5050',
													mb: 1,
													textTransform: 'uppercase',
												}}>
												{item.label}
											</Typography>
										)}

										{/* CONTENT */}
										{(item.content || []).map(
											(content, contentIndex) => (
												<Box
													key={contentIndex}
													sx={{
														display: 'flex',
														alignItems:
															'flex-start',
														gap: 1,
														mb: 1,
														background:
															'transparent',
														borderRadius: 0,
														p: 0,
													}}>
													<Check
														sx={{
															fontSize: 15,
															color: teal,
															mt: '10px',
														}}
													/>

													{onRenderSections(
														sectionIndex,
														itemIndex,
														contentIndex,
														content,
													)}
												</Box>
											),
										)}
									</Box>
								))}
							</Box>
						</Box>
					))}
				{isLoading && (
					<Box
						sx={{
							width: '100%',
						}}>
						<Skeleton animation="wave" width={'100%'} height={80} />
					</Box>
				)}
			</Stack>

			{/* Bottom Actions */}
			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={3}>
				<Button
					onClick={onStorePlan}
					variant="contained"
					startIcon={<SaveIcon />}
					disabled={isLoading}
					sx={{
						textTransform: 'none',
						borderRadius: '8px',
						fontWeight: 600,
						fontSize: 13,
						px: 2,
						py: 0.9,
						boxShadow: 'none',
						backgroundColor: teal,
						color: dark,
						'&:hover': {
							backgroundColor: '#00b0a0',
							borderColor: '#00b0a0',
							boxShadow: 'none',
						},
					}}>
					Save to {elder}&apos;s Profile
				</Button>

				<Button
					onClick={onDownload}
					variant="outlined"
					startIcon={<DownloadIcon />}
					disabled={isLoading}
					sx={{
						textTransform: 'none',
						borderRadius: '8px',
						fontWeight: 500,
						fontSize: 13,
						px: 1.8,
						py: 0.9,
						boxShadow: 'none',
						border: `1px solid ${border}`,
						backgroundColor: '#fff',
						color: '#1a2424',
						'&:hover': {
							backgroundColor: surface,
							borderColor: teal,
							boxShadow: 'none',
						},
					}}>
					Download PDF
				</Button>

				{/* <Button
					variant="outlined"
					startIcon={<PrintIcon />}
					sx={{
						textTransform: 'none',
						borderRadius: '8px',
						fontWeight: 500,
						fontSize: 13,
						px: 1.8,
						py: 0.9,
						boxShadow: 'none',
						border: `1px solid ${border}`,
						backgroundColor: '#fff',
						color: '#1a2424',
						'&:hover': {
							backgroundColor: surface,
							borderColor: teal,
							boxShadow: 'none',
						},
					}}>
					Print
				</Button> */}
			</Stack>
		</Box>
	);
}
